import ConfigurationSummaryList from '@/components/data/configuration/summary/ConfigurationSummaryList';
import DiscrepancyNoteGrid from '@/components/data/discrepancyNote/grid/DiscrepancyNoteGrid';
import PatientDataList from '@/components/data/patient/data/PatientDataList';
import Breadcrumbs from '@/components/layouts/Breadcrumbs';
import TrialContext from '@/contexts/data/TrialContext';
import useConfigurationSummaries from '@/hooks/data/useConfigurationSummaries';
import useMetadata from '@/hooks/data/useMetadata';
import usePatient from '@/hooks/data/usePatient';
import usePatientDataSummaries from '@/hooks/data/usePatientDataSummaries';
import useSourceDataNotesByPatientStudyNumber from '@/hooks/data/useSourceDataNotesByPatientStudyNumber';
import { faBoxArchive, faClipboardListCheck, faCogs, faFlag, faGlobeOceania, faPlus, faUser, faUsers } from '@fortawesome/duotone-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Grid, IconButton, Paper, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { FC, useContext, useCallback, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';
import { SourceDataVerificationPermission } from '@/api/dtos';
import usePermissions from '@/hooks/utility/usePermissions';


const Title = styled(Typography)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between'
}));

const PatientNotes: FC = () => {
    
    const { trialCode, patientStudyNumber } = useParams();

    const { data: trial } = useContext(TrialContext);
    const { data: patient, loading: patientLoading, validating: patientUpdating } = usePatient(patientStudyNumber!, { revalidateOnFocus: false });
    const { data: metadata, loading: metadataLoading, validating: metadataUpdating } = useMetadata(trialCode!, { revalidateOnFocus: false });
    const { data: notes, loading: notesLoading, validating: notesUpdating } = useSourceDataNotesByPatientStudyNumber(patientStudyNumber!, { revalidateOnFocus: false });

    const loadingData = notesLoading || metadataLoading || patientLoading;
    const updating = !loadingData && (notesUpdating || metadataUpdating || patientUpdating);
    
    const [ settingsOpen, setSettingsOpen ] = useState(false);

    const theme = useTheme();

    const breadcrumbs = useMemo(() => {
        return [
            {
                label: 'Participants',
                link: `/${trialCode}/participants`,
                icon: faUsers
            },
            {
                label: patientStudyNumber,
                link: `/${trialCode}/participants/${patientStudyNumber}`,
                icon: faUser
            },
            {
                label: 'Discrepancy Notes',
                icon: faFlag
            }
        ];
    }, [patientStudyNumber, trialCode]);

    const { 
        data: [
            hasViewSourceDataNote,
            hasViewSourceDataNoteInternal
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.ViewSourceDataNote,
            SourceDataVerificationPermission.ViewSourceDataNoteInternal
        ], 
        loadingData,
        trial?.id,
        undefined,
        undefined,
        undefined,
        patient?.institutionId,
        patient?.id,
        undefined,
        undefined,
        undefined,
        {
            revalidateOnFocus: false
        }
    );
    
    if (!hasViewSourceDataNote && !hasViewSourceDataNoteInternal && !permissionsLoading && !permissionsValidating) {
        return (
            <Navigate to='/error/403' />
        )
    }
    
    const loading = loadingData || permissionsLoading || permissionsValidating;
    
    return (
        <>
            <PageTitle title={`Discrepancy Notes${TITLE_SEPARATOR}${patientStudyNumber}${TITLE_SEPARATOR}${trial?.name}`} />
            <Breadcrumbs 
                breadcrumbs={breadcrumbs}
            />
            <Title 
                variant="h1" 
                gutterBottom
                color={theme.palette.secondary.main}
            >
                Discrepancy Notes
                <Box>
                    <IconButton onClick={() =>  setSettingsOpen(true)}>
                        <FontAwesomeIcon icon={faCogs} />
                    </IconButton>
                </Box>
            </Title>
            <DiscrepancyNoteGrid 
                masterGroups={undefined}
                coordinatingGroups={undefined}
                countries={undefined}
                institutions={undefined}
                metadata={metadata}
                notes={notes}
                loading={loading}
                updating={updating}
                settingsOpen={settingsOpen}
                onSettingsClose={() => setSettingsOpen(false)}
                patientView
            />
        </>
    );
}

export default PatientNotes;